import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Stack,
  Title,
  Card,
  Button,
  Group,
  Alert,
  Text,
  Badge,
  Skeleton,
  Select,
  SimpleGrid,
  NumberInput,
  Pagination,
  ActionIcon,
  Paper,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconRefresh,
  IconClock,
  IconUsers,
  IconDatabase,
  IconChevronLeft,
  IconSearch,
  IconArrowUp,
  IconArrowDown,
  IconPhoto,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useAppUser } from '../hooks/useAppUser';
import { usePageTitle } from '../hooks/usePageTitle';
import { RecipeImage } from '../components/UI/RecipeImage';
import { MoreRestrictionsMenu } from '../components/UI/MoreRestrictionsMenu';
import { RecipeBadge, splitBadgesForDisplay } from '../utils/recipeBadges';
import { TopPickRibbon } from '../components/UI/TopPickRibbon';
import { formatTime } from '../utils/formatTime';
import { Recipe } from '../types/mealPlan';
import { fetchCatalogRecipes, fetchCatalogCuisines } from '../services/adminCatalogService';
import { CATEGORY_CHOICES } from '../components/Admin/CategorizationMenu';
import {
  DIETARY_RESTRICTIONS,
  NUTRITION_GOALS,
  ALLERGEN_FREE_TAGS,
} from '../utils/dietaryOptions';
import { CuisineBadge } from '../components/UI/CuisineBadge';

const PAGE_SIZE = 24;
const STORAGE_KEY = 'catalog-browser-state';

const difficultyColor = (d?: string) =>
  d === 'easy' ? 'green' : d === 'medium' ? 'yellow' : 'red';

const loadSavedState = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

export const CatalogBrowserPage = () => {
  usePageTitle('Catalog | Meals You Love');
  const { user, loading: userLoading } = useAppUser();
  const navigate = useNavigate();
  const saved = useRef(loadSavedState());
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [cuisine, setCuisine] = useState<string | null>(saved.current?.cuisine ?? null);
  const [difficulty, setDifficulty] = useState<string | null>(saved.current?.difficulty ?? null);
  const [source, setSource] = useState<string | null>(saved.current?.source ?? null);
  const [category, setCategory] = useState<string | null>(saved.current?.category ?? null);
  const [slot, setSlot] = useState<string | null>(saved.current?.slot ?? null);
  const [dietaryRestriction, setDietaryRestriction] = useState<string | null>(
    saved.current?.dietaryRestriction ?? null
  );
  const [servings, setServings] = useState<number | ''>(saved.current?.servings ?? '');
  const [search, setSearch] = useState(saved.current?.search ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(saved.current?.search ?? '');
  const [sortField, setSortField] = useState(saved.current?.sortField ?? 'created');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(saved.current?.sortDirection ?? 'desc');
  const [cuisineOptions, setCuisineOptions] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(saved.current?.page ?? 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [regeneratingImageId, setRegeneratingImageId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    fetchCatalogCuisines(user).then(setCuisineOptions).catch(() => {});
  }, [user]);


  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadPage = useCallback(async (page: number) => {
    if (!user) return;
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const { recipes: data, total } = await fetchCatalogRecipes(user, {
        limit: PAGE_SIZE,
        offset,
        cuisine: cuisine || undefined,
        difficulty: difficulty || undefined,
        servings: servings || undefined,
        source: source || undefined,
        category: category || undefined,
        slot: slot || undefined,
        dietaryRestriction: dietaryRestriction || undefined,
        search: debouncedSearch || undefined,
        sort: `${sortField}:${sortDirection}`,
      });
      setRecipes(data);
      setTotalCount(total);
      setTotalPages(Math.max(1, Math.ceil(total / PAGE_SIZE)));
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load catalog recipes',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [user, cuisine, difficulty, servings, source, category, slot, dietaryRestriction, debouncedSearch, sortField, sortDirection]);

  // Persist filter/pagination state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      cuisine, difficulty, servings, source, category, slot, dietaryRestriction, search, sortField, sortDirection, page: currentPage,
    }));
  }, [cuisine, difficulty, servings, source, category, slot, dietaryRestriction, search, sortField, sortDirection, currentPage]);

  // Build a stable key from filter values to detect actual filter changes
  const filterKey = `${cuisine}|${difficulty}|${servings}|${source}|${category}|${slot}|${dietaryRestriction}|${debouncedSearch}|${sortField}|${sortDirection}`;
  const prevFilterKey = useRef(filterKey);
  const hasLoadedInitialPage = useRef(false);

  useEffect(() => {
    if (!user) return;
    const filtersChanged = prevFilterKey.current !== filterKey;
    prevFilterKey.current = filterKey;

    if (!hasLoadedInitialPage.current) {
      hasLoadedInitialPage.current = true;
      loadPage(currentPage);
    } else if (filtersChanged) {
      setCurrentPage(1);
      loadPage(1);
    }
  }, [user, filterKey, loadPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadPage(page);
    window.scrollTo({ top: 0 });
  };

  const isLoading = userLoading || loading;

  if (!userLoading && !user?.isAdmin) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconAlertCircle size="1rem" />} title="Access Denied" color="red" radius="lg">
          You do not have admin privileges.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Group justify="space-between" align="flex-start">
            <Group>
              <ActionIcon variant="subtle" size="lg" onClick={() => navigate(-1)}>
                <IconChevronLeft size={20} />
              </ActionIcon>
              <Stack gap={0}>
                <Title order={2}>Recipe Catalog</Title>
                <Text c="dimmed" size="sm">Browse all recipes in the shared catalog</Text>
              </Stack>
            </Group>
            <Button
              leftSection={<IconRefresh size="1rem" />}
              variant="outline"
              onClick={() => loadPage(currentPage)}
            >
              Refresh
            </Button>
          </Group>
        </div>

        <Paper p="md" radius="lg" withBorder>
          <Group gap="sm">
            <TextInput
              placeholder="Search recipes..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={250}
            />
            <Select
              placeholder="Cuisine"
              clearable
              searchable
              value={cuisine}
              onChange={setCuisine}
              data={cuisineOptions}
              w={200}
            />
            <Select
              placeholder="Difficulty"
              clearable
              value={difficulty}
              onChange={setDifficulty}
              data={[
                { value: 'easy', label: 'Easy' },
                { value: 'medium', label: 'Medium' },
                { value: 'hard', label: 'Hard' },
              ]}
              w={140}
            />
            <Select
              placeholder="Course"
              clearable
              value={category}
              onChange={setCategory}
              data={[
                ...CATEGORY_CHOICES,
                { value: 'uncategorized', label: 'Uncategorized' },
              ]}
              w={150}
            />
            <Select
              placeholder="Serves"
              clearable
              value={slot}
              onChange={setSlot}
              data={[
                { value: 'breakfast', label: 'Breakfast' },
                { value: 'lunch', label: 'Lunch' },
                { value: 'dinner', label: 'Dinner' },
                { value: 'none', label: 'No slot' },
              ]}
              w={130}
            />
            <Select
              placeholder="Dietary"
              clearable
              searchable
              value={dietaryRestriction}
              onChange={setDietaryRestriction}
              data={[
                { group: 'Restrictions', items: DIETARY_RESTRICTIONS },
                { group: 'Nutrition goals', items: NUTRITION_GOALS },
                { group: 'Allergen-free', items: ALLERGEN_FREE_TAGS },
              ]}
              w={180}
            />
            <Select
              placeholder="Source"
              clearable
              value={source}
              onChange={setSource}
              data={[
                { value: 'usda_myplate', label: 'USDA MyPlate' },
                { value: 'nih_nhlbi', label: 'NIH Heart-Healthy' },
              ]}
              w={170}
            />
            <NumberInput
              placeholder="Servings"
              value={servings}
              onChange={(v) => setServings(v === '' ? '' : Number(v))}
              min={1}
              max={12}
              w={120}
            />
            {!debouncedSearch && (
              <Group gap="xs">
                <Select
                  value={sortField}
                  onChange={(value) => setSortField(value || 'created')}
                  data={[
                    { value: 'created', label: 'Date added' },
                    { value: 'usage', label: 'Usage count' },
                  ]}
                  size="sm"
                  w={150}
                />
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  title={`Sort ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}
                >
                  {sortDirection === 'asc' ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
                </ActionIcon>
              </Group>
            )}
            {totalCount !== null && (
              <Text size="sm" c="dimmed" ml="auto" style={{ whiteSpace: 'nowrap' }}>
                {totalCount.toLocaleString()} {totalCount === 1 ? 'recipe' : 'recipes'}
              </Text>
            )}
          </Group>
        </Paper>

        {isLoading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} padding="lg" radius="lg" withBorder>
                <Card.Section>
                  <Skeleton height={200} radius={0} />
                </Card.Section>
                <Stack gap="xs" mt="md">
                  <Stack gap="xs">
                    <Skeleton height={18} width="80%" />
                    <Skeleton height={14} width="95%" />
                    <Skeleton height={14} width="60%" />
                  </Stack>
                  <Stack gap="xs">
                    <Group gap="md">
                      <Group gap="xs">
                        <Skeleton height={16} width={16} radius="sm" />
                        <Skeleton height={14} width={50} />
                      </Group>
                      <Group gap="xs">
                        <Skeleton height={16} width={16} radius="sm" />
                        <Skeleton height={14} width={20} />
                      </Group>
                      <Skeleton height={20} width={50} radius="xl" />
                    </Group>
                    <Group gap="xs">
                      <Skeleton height={20} width={70} radius="xl" />
                      <Skeleton height={20} width={55} radius="xl" />
                      <Skeleton height={20} width={65} radius="xl" />
                    </Group>
                  </Stack>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        ) : recipes.length === 0 ? (
          <Alert icon={<IconDatabase size="1rem" />} title="No recipes" color="blue" radius="lg">
            No catalog recipes match the current filters.
          </Alert>
        ) : (
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
              {recipes.map((recipe) => (
                <Card
                  key={recipe.id}
                  padding="lg"
                  radius="lg"
                  withBorder
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/catalog-recipe/${recipe.id}`)}
                >
                  <Card.Section style={{ position: 'relative' }}>
                    <RecipeImage
                      src={recipe.imageUrl}
                      alt={recipe.name}
                      height={200}
                    />
                    {recipe.cuisine && (
                      <CuisineBadge
                        cuisine={recipe.cuisine}
                        placement="overlay"
                        style={{ position: 'absolute', top: 8, left: 8 }}
                      />
                    )}
                    {recipe.isCurated && <TopPickRibbon />}
                    <Tooltip label="Regenerate image" position="left">
                      <ActionIcon
                        variant="filled"
                        color="dark"
                        size="sm"
                        radius="xl"
                        style={{ position: 'absolute', top: 8, right: 8, opacity: 0.8 }}
                        loading={regeneratingImageId === recipe.id}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!user) return;
                          setRegeneratingImageId(recipe.id);
                          try {
                            const token = await user.getIdToken();
                            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/recipes/regenerate-image`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                              },
                              body: JSON.stringify({ catalogRecipeId: recipe.id }),
                            });
                            if (!response.ok) {
                              const data = await response.json().catch(() => ({}));
                              throw new Error(data.error || 'Failed to regenerate image');
                            }
                            const data = await response.json();
                            setRecipes(prev => prev.map(r =>
                              r.id === recipe.id ? { ...r, imageUrl: data.imageUrl } : r
                            ));
                            notifications.show({
                              title: 'Image regenerated',
                              message: `New image generated for "${recipe.name}"`,
                              color: 'green',
                              icon: <IconCheck size={18} />,
                              autoClose: 3000,
                            });
                          } catch (error: any) {
                            notifications.show({
                              title: 'Error',
                              message: error.message || 'Failed to regenerate image',
                              color: 'red',
                              icon: <IconX size={18} />,
                              autoClose: 5000,
                            });
                          } finally {
                            setRegeneratingImageId(null);
                          }
                        }}
                      >
                        <IconPhoto size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Card.Section>

                  <Stack gap="xs" mt="md" style={{ flex: 1, justifyContent: 'space-between' }}>
                    <Stack gap="xs">
                      <Text fw={500} lineClamp={2}>
                        {recipe.name}
                      </Text>
                      {recipe.description && (
                        <Text size="sm" c="dimmed" lineClamp={2}>
                          {recipe.description}
                        </Text>
                      )}
                    </Stack>

                    <Stack gap="xs">
                      <Group gap="md">
                        <Group gap="xs">
                          <IconClock size={16} />
                          <Text size="sm">{formatTime(recipe.totalTimeMinutes)}</Text>
                        </Group>
                        <Group gap="xs">
                          <IconUsers size={16} />
                          <Text size="sm">{recipe.servings}</Text>
                        </Group>
                        {recipe.difficulty && (
                          <Badge
                            size="sm"
                            variant="light"
                            color={difficultyColor(recipe.difficulty)}
                          >
                            {recipe.difficulty}
                          </Badge>
                        )}
                        {(recipe as any).usageCount != null && (
                          <Badge size="sm" variant="light" color="gray">
                            {(recipe as any).usageCount} {(recipe as any).usageCount === 1 ? 'use' : 'uses'}
                          </Badge>
                        )}
                      </Group>

                      <Group gap="xs">
                        {(() => {
                          const dietaryRestrictions = recipe.dietaryRestrictions || [];
                          const tags = recipe.tags || [];
                          const allBadges: RecipeBadge[] = [
                            ...dietaryRestrictions.map((r) => ({ text: r, type: 'dietary' as const })),
                            ...tags.map((t) => ({ text: t, type: 'tag' as const })),
                          ];
                          const { visible, hiddenRestrictions } = splitBadgesForDisplay(allBadges, 3);
                          return (
                            <>
                              {visible.map((badge) => (
                                <Badge key={`${badge.type}-${badge.text}`} size="sm" variant="outline">
                                  {badge.text}
                                </Badge>
                              ))}
                              <MoreRestrictionsMenu restrictions={hiddenRestrictions} />
                            </>
                          );
                        })()}
                      </Group>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
            {totalPages > 1 && !debouncedSearch && (
              <Group justify="center" mt="md">
                <Pagination
                  total={totalPages}
                  value={currentPage}
                  onChange={handlePageChange}
                  siblings={1}
                />
              </Group>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
};
